import {makeAutoObservable, observable, reaction} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {ModalType} from "data/enums";
import type {IPlayerCalculations, IGamePlayer} from "data/utils/player.calculations";

export interface IModalRosterStatsController extends ViewController {
	get contestId(): number;
	get isOpen(): boolean;
	get player(): IGamePlayer | undefined;

	onClose: () => void;
}

@injectable()
export class ModalRosterStatsController implements IModalRosterStatsController {
	@observable private _playerID = 0;
	@observable private _disposers: ReturnType<typeof reaction>[] = [];
	@observable private _contestId: number = 0;

	constructor(
		@inject(Bindings.ModalsStore) private readonly _modalsStore: IModalsStore,
		@inject(Bindings.PlayerCalculations)
		private readonly _playerCalculations: IPlayerCalculations
	) {
		makeAutoObservable(this);
	}

	get isOpen(): boolean {
		return this._modalsStore.modal === ModalType.PLAYER_STATS;
	}

	get player() {
		return this._playerCalculations.getGamePlayerById(this._playerID, this._contestId);
	}

	init(param: void) {
		this._disposers = [
			reaction(
				() => this._modalsStore.modal,
				() => {
					this._playerID = this.playerId;
					this._contestId = this.contestId;
				}
			),
		];
	}

	dispose() {
		this._disposers.forEach((dispose) => dispose());
	}

	private get playerId(): number {
		const modalContent = this._modalsStore.modalContent;
		if (!modalContent || !modalContent.meta) {
			return 0;
		}
		if ("id" in modalContent.meta) {
			return Number(modalContent.meta.id) ?? 0;
		}

		return 0;
	}

	get contestId(): number {
		const modalContent = this._modalsStore.modalContent;
		if (!modalContent || !modalContent.meta) {
			return 0;
		}

		if ("contestId" in modalContent.meta) {
			return Number(modalContent.meta.contestId) ?? 0;
		}

		return 0;
	}

	onClose = () => {
		this._modalsStore.hideModal();
	};
}
